<script setup>
import { onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  title: { type: String, required: true },
  message: { type: String, required: true },
  confirmLabel: { type: String, default: "Delete" },
  isBusy: { type: Boolean, default: false },
  busyLabel: { type: String, default: "Working…" },
  danger: { type: Boolean, default: true },
});
const emit = defineEmits(["close", "confirm"]);

function close() { emit("close"); }
function onEsc(e) { if (e.key === "Escape") close(); }
onMounted(() => document.addEventListener("keydown", onEsc));
onBeforeUnmount(() => document.removeEventListener("keydown", onEsc));

function confirm() {
  if (props.isBusy) return;
  emit("confirm");
}
</script>

<template>
  <!-- Teleported to <body> — rendered inline, this would otherwise inherit
       whatever the caller's ancestor context sets (e.g. a table cell's
       text-right, used to align a "..." menu trigger), which bled into this
       modal's own text and pushed the title/message to the right. -->
  <Teleport to="body">
    <div class="fixed inset-0 bg-black/20 dark:bg-black/60 z-[500] flex items-center justify-center p-4" @click.self="close">
      <div class="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[420px] flex flex-col overflow-hidden z-[520]">

        <button
          class="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer transition-colors"
          @click="close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>

        <div class="p-6 pt-8 text-left">
          <div
            class="flex items-center justify-center w-12 h-12 rounded-full mb-4"
            :class="danger ? 'bg-red-100 dark:bg-red-950/60 text-red-500 dark:text-red-400' : 'bg-blue-100 dark:bg-blue-950/60 text-blue-500 dark:text-blue-400'"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M12 9v4M12 17h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2" />
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800 dark:text-slate-100 m-0 mb-2">{{ title }}</h2>
          <p class="text-sm text-slate-500 dark:text-slate-400 m-0">{{ message }}</p>
        </div>

        <div class="flex items-center justify-end gap-3 px-6 pb-6">
          <button
            class="px-6 py-2.5 border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
            @click="close"
          >
            Cancel
          </button>
          <button
            :disabled="isBusy"
            :class="danger ? 'bg-red-600 hover:bg-red-500' : 'bg-slate-800 dark:bg-slate-700 hover:bg-slate-700 dark:hover:bg-slate-600'"
            class="px-6 py-2.5 text-white rounded-xl text-sm font-semibold transition-colors cursor-pointer disabled:opacity-50"
            @click="confirm"
          >
            {{ isBusy ? busyLabel : confirmLabel }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
